import { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';
import format from 'date-fns/format';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import ExportCSV from '@features/ExportCSV';
import { ControlledToaster } from '@components/Toaster';
import { useTranslation, useSurveysOptions, useQueryParams, useLocations } from '@hooks';
import { PLUSPOINT_INTEGRATION, AccIntegrationName } from '@constants/integrations';
import { INCLUDE_COVER_MANAGER } from '@constants/feedbacks';
import { channelsActions, feedbacksActions, labelsActions } from '@actions';
import { feedbacksService } from '@services';
import FiltersPanel from './FiltersPanel';
import Feedbacks from './Feedbacks';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const MINUTE = 1000 * 60;

const FeedbacksPage = () => {
    const params = useQueryParams();
    const queryParamsFeedbackId = params.get('feedbackId');

    const [filter, setFilter] = useState({
        companies: [],
        surveys: [],
        timeframe: { start: null, end: null },
        rating: params.get('rating') ? JSON.parse(params.get('rating')) : [1, 2, 3, 4, 5],
        statuses: params.get('statuses') ? JSON.parse(params.get('statuses')) : [0, 1, 2, 3],
        hideAnonimous: params.get('hideAnonimous') ? JSON.parse(params.get('hideAnonimous')) : true,
        feedbackId: queryParamsFeedbackId ? queryParamsFeedbackId : undefined,
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const account = useSelector(state => state.account?.account);
    const { feedbacks, pageToken, loading, newFeedbacksExist } = useSelector(state => state.feedbacks);
    const language = useSelector(state => state.auth.user?.language);

    const { t } = useTranslation();

    const history = useHistory();
    const dispatch = useDispatch();
    const { getSurveysByLocationIds } = useSurveysOptions();
    const { mapLocationsToFormValue } = useLocations();
    const firstRender = useRef(true);

    const isCoverManagerConnected = account?.integrations.find(
        i => i.type === AccIntegrationName.CoverManager,
    )?.isActive;

    useEffect(() => {
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
    }, []);

    useEffect(() => {
        if (locations.length) {
            const availableSurveys = getSurveysByLocationIds(locations.map(l => l.companyId));
            const surveys = isCoverManagerConnected ? [...availableSurveys, INCLUDE_COVER_MANAGER] : availableSurveys;
            const queryParamsFilter = [params.get('rating'), params.get('hideAnonimous'), params.get('statuses')];
            setFilterNumber(queryParamsFilter.filter(Boolean).length);
            setFilter(prev => ({ ...prev, surveys, companies: mapLocationsToFormValue() }));
            if (params.get('feedbackId')) {
                history.replace();
            }
        } else {
            dispatch(feedbacksActions.resetLoading());
        }
    }, [dispatch]);

    const currentQuery = useMemo(() => {
        return {
            ...filter,
            companies: filter.companies.map(company => company.id),
            surveys: isCoverManagerConnected ? filter.surveys.filter(i => i !== INCLUDE_COVER_MANAGER) : filter.surveys,
            integrationNames: [PLUSPOINT_INTEGRATION],
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'dd/MMM/yyyy') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'dd/MMM/yyyy') : null,
            includeAnonymous: !filter.hideAnonimous,
            includeCoverManager: isCoverManagerConnected ? filter.surveys.includes(INCLUDE_COVER_MANAGER) : false,
        };
    }, [filter, isCoverManagerConnected]);

    useEffect(() => {
        if (queryParamsFeedbackId && !firstRender.current) {
            dispatch(feedbacksActions.getAll({ ...currentQuery, feedbackId: queryParamsFeedbackId }));
            history.replace();
        }
    }, [queryParamsFeedbackId]);

    useEffect(() => {
        if (currentQuery.companies.length && currentQuery.rating.length && currentQuery.statuses.length) {
            if (firstRender.current) {
                dispatch(feedbacksActions.getAll(currentQuery));
                firstRender.current = false;
            }
        }
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const newExists = await feedbacksService.checkNewExist(currentQuery);
                dispatch(feedbacksActions.checkNewExistSuccess(newExists));
            } catch (_) {
                logger.error('Failed to check new feedbacks');
            }
        }, MINUTE);
        return () => clearInterval(interval);
    }, [currentQuery, dispatch]);

    useEffect(() => {
        if (newFeedbacksExist) {
            setOpenAlert(true);
        }
    }, [newFeedbacksExist]);

    const getNewReviews = () => {
        setOpenAlert(false);
        dispatch(feedbacksActions.resetNewExist());
        dispatch(feedbacksActions.getAll(currentQuery));
    };

    const onCloseToaster = () => dispatch(feedbacksActions.resetNewExist());

    const loadMore = () => {
        if (!pageToken) return;
        dispatch(feedbacksActions.loadMore(currentQuery, pageToken));
    };

    const onApplyFilters = () => {
        dispatch(feedbacksActions.getAll({ ...currentQuery, feedbackId: undefined }));
    };

    const surveyExists = useMemo(() => {
        return locations?.map(company => company.surveys)?.flat().length > 0;
    }, [locations]);

    const getFeedType = () => {
        if (!surveyExists) {
            return 3;
        }
        if (!filter.companies.length || !filter.rating.length || !filter.statuses.length) {
            return 1;
        }
        if (!feedbacks?.length && !firstRender.current) {
            return 3;
        }
        return 0;
    };

    const onUpdateContactCallback = () => dispatch(feedbacksActions.refetch(currentQuery, null, feedbacks.length));

    return (
        <Container>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    locations={locations}
                    filter={filter}
                    setFilter={setFilter}
                    type="feedbacks"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <ExportCSV callback={() => feedbacksService.exportCSV(currentQuery)} type="feedbacks" />
            </div>
            <Feedbacks
                data={feedbacks}
                language={language}
                loading={loading}
                pageToken={pageToken}
                fetchData={loadMore}
                feedType={getFeedType()}
                onUpdateContactCallback={onUpdateContactCallback}
                onInitConversationCallback={onUpdateContactCallback}
            />
            <ControlledToaster
                callback={getNewReviews}
                autoHideDuration={null}
                text={
                    <Trans t={t} i18nKey={'FeedbackResponses.newReviews'}>
                        <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={getNewReviews}>
                            {{ update: t('FeedbackResponses.update') }}
                        </b>
                    </Trans>
                }
                type="info"
                open={openAlert}
                setOpen={setOpenAlert}
                clickClose={onCloseToaster}
            />
        </Container>
    );
};

export default FeedbacksPage;
